import React, { useState } from "react";
import { toast } from "sonner";
import Input from "../../../input";
import useJouneyReq from "../../../hook/bot-req";

interface LeadFormData {
  phone: string;
  first_name: string;
  last_name: string;
  email: string;
  source: string;
}

interface SubmitLeadResponse {
  workflow_id: string;
  lead_correlation_id: string;
  status: string;
  message?: string;
}

interface LeadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  journeyId?: string;
  onSuccess?: (data: SubmitLeadResponse, lead: LeadFormData) => void;
}

const LEAD_SOURCE_OPTIONS = ["Manual"];
const LEAD_CONTACTS_STORAGE_KEY = "journey_lead_contacts";

const normalizeLeadSource = (source: unknown) => {
  if (typeof source !== "string") return "Manual";

  const legacySourceMap: Record<string, string> = {
    manual: "Manual",
    website_form: "Website Form",
    landing_page: "Website Form",
    referral: "Referral",
    api: "Manual",
    linkedin: "LinkedIn",
  };

  return legacySourceMap[source] ?? source;
};

const buildLeadPayload = (lead: LeadFormData): LeadFormData => ({
  phone: lead.phone.trim(),
  first_name: lead.first_name.trim(),
  last_name: lead.last_name.trim(),
  email: lead.email.trim(),
  source: normalizeLeadSource(lead.source),
});

const saveLeadContactRecord = (lead: LeadFormData, response: SubmitLeadResponse) => {
  if (!response?.lead_correlation_id) return;

  const record = {
    ...lead,
    workflow_id: response.workflow_id,
    lead_correlation_id: response.lead_correlation_id,
    submitted_at: new Date().toISOString(),
  };

  try {
    const saved = localStorage.getItem(LEAD_CONTACTS_STORAGE_KEY);
    const contacts = saved ? JSON.parse(saved) : [];
    const nextContacts = [
      record,
      ...(Array.isArray(contacts)
        ? contacts.filter((item: any) => item?.lead_correlation_id !== response.lead_correlation_id)
        : []),
    ];

    localStorage.setItem(LEAD_CONTACTS_STORAGE_KEY, JSON.stringify(nextContacts));
    localStorage.setItem(`lead_correlation_${response.lead_correlation_id}`, JSON.stringify(record));
  } catch {
    localStorage.setItem(`lead_correlation_${response.lead_correlation_id}`, JSON.stringify(record));
  }
};

export const LeadFormModal: React.FC<LeadFormModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const token = localStorage.getItem("authToken");
  const { makeRequest: submitLead, loading: submitLoading } = useJouneyReq(`/leads`, "POST", {
    Authorization: `Bearer ${token}`,
  });

  const [formData, setFormData] = useState<LeadFormData>({
    phone: "",
    first_name: "",
    last_name: "",
    email: "",
    source: "Manual",
  });
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.phone) {
      toast.error("Phone number is required");
      return;
    }

    setLoading(true);

    try {
      const payload = buildLeadPayload(formData);
      const [response, status] = await submitLead(payload);

      if (status >= 400 || response?.status === "error") {
        toast.error(response?.message || response?.detail || "Failed to submit lead");
        return;
      }

      saveLeadContactRecord(payload, response);
      toast.success(response?.message || "Lead submitted successfully! Journey started.");

      onSuccess?.(response, payload);
      onClose();

      // Reset form
      setFormData({
        phone: "",
        first_name: "",
        last_name: "",
        email: "",
        source: "Manual",
      });
    } catch (err: any) {
      toast.error(err.message || "Failed to submit lead");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add New Lead</h2>
          <button
            onClick={onClose}
            className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="tel"
              label="Phone Number"
              placeholder="+234 123 456 7890"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                type="text"
                label="First Name"
                placeholder="John"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full"
              />
            </div>
            <div>
              <Input
                type="text"
                label="Last Name"
                placeholder="Doe"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full"
              />
            </div>
          </div>

          <div>
            <Input
              type="email"
              label="Email Address"
              placeholder="john.doe@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Source
            </label>
            <div className="relative">
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 appearance-none bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {LEAD_SOURCE_OPTIONS.map((source) => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || submitLoading}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading || submitLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </div>
              ) : (
                "Submit Lead"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};